/**
 * StickyTabs — custom element for the sticky_tabs section.
 *
 * CSS `position: sticky` is unreliable inside Shopify theme layouts because
 * the .shopify-section wrapper is the same height as the bar, leaving no
 * scroll range. Instead we use a JS-driven position:fixed approach with an
 * invisible placeholder sibling that fills the layout gap when the bar lifts.
 *
 * Behaviour:
 *  • Bar stays in normal flow until the user scrolls it to just below the
 *    theme header; then it becomes position:fixed at top = header height.
 *  • Clicking a tab smooth-scrolls to the target, offsetting by the combined
 *    height of the header + bar so the target is never hidden underneath.
 *  • The tab whose section occupies the top of the visible viewport is
 *    "active" (full opacity/bold). All others are dimmed to 50% opacity.
 */

if (!customElements.get('sticky-tabs')) {
  class StickyTabs extends HTMLElement {
    connectedCallback() {
      this._btns     = [...this.querySelectorAll('.st-btn[data-anchor]')];
      this._sections = this._btns
        .map((btn) => ({ btn, el: document.getElementById(btn.dataset.anchor) }))
        .filter((s) => s.el);

      this._fixed      = false;
      this._naturalTop = null; // measured while element is in flow

      // Placeholder sibling — shown when the bar goes fixed so layout doesn't jump
      this._ph = document.createElement('div');
      this._ph.setAttribute('aria-hidden', 'true');
      this._ph.style.cssText = 'display:none;pointer-events:none;';
      this.insertAdjacentElement('beforebegin', this._ph);

      requestAnimationFrame(() => {
        this._updateHeaderHeight();
        this._checkSticky();
        this._bindClicks();
        this._bindScroll();

        // Keep header height in sync (announcement bars, etc.)
        if (typeof ResizeObserver !== 'undefined') {
          const header = document.getElementById('header');
          if (header) {
            new ResizeObserver(() => {
              this._updateHeaderHeight();
              this._resetSticky();
            }).observe(header);
          }
        }

        window.addEventListener('scroll', () => this._checkSticky(), { passive: true });
        window.addEventListener('resize', () => this._resetSticky(), { passive: true });
      });
    }

    // ── Header height ─────────────────────────────────────────────────────

    _updateHeaderHeight() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim();
      this._headerHeight = parseFloat(raw) || 0;
    }

    // ── Sticky logic ──────────────────────────────────────────────────────

    _checkSticky() {
      // Only measure natural position while the bar is still in flow
      if (!this._fixed && this._naturalTop === null) {
        this._naturalTop = this.getBoundingClientRect().top + window.scrollY;
      }

      const threshold  = (this._naturalTop ?? 0) - this._headerHeight;
      const shouldFix  = window.scrollY >= threshold;

      if (shouldFix === this._fixed) return;
      this._fixed = shouldFix;

      if (shouldFix) {
        // Lift bar to fixed; placeholder fills the gap
        this._ph.style.display = 'block';
        this._ph.style.height  = this.offsetHeight + 'px';
        this.style.position    = 'fixed';
        this.style.top         = this._headerHeight + 'px';
        this.style.left        = '0';
        this.style.width       = '100%';
        this.style.zIndex      = '40'; /* below cart backdrop (52) and drawer (90) */
      } else {
        // Return to normal flow
        this._ph.style.display = 'none';
        this.style.cssText     = '';
      }
    }

    _resetSticky() {
      // Force re-measurement on next check
      this._fixed      = false;
      this._naturalTop = null;
      this.style.cssText      = '';
      this._ph.style.display  = 'none';
      this._updateHeaderHeight();
      requestAnimationFrame(() => this._checkSticky());
    }

    // ── Click → smooth scroll with offset ────────────────────────────────

    _bindClicks() {
      this._btns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.getElementById(btn.dataset.anchor);
          if (!target) return;

          const barHeight  = this.offsetHeight;
          const totalOff   = this._headerHeight + barHeight + 8; // 8 px breathing room
          const targetTop  =
            target.getBoundingClientRect().top + window.scrollY - totalOff;

          window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        });
      });
    }

    // ── Scroll → active tab ───────────────────────────────────────────────

    _bindScroll() {
      const update = () => {
        const barHeight  = this.offsetHeight;
        const triggerY   = window.scrollY + this._headerHeight + barHeight + 8;

        let activeId = null;
        for (const { el } of this._sections) {
          if (el.getBoundingClientRect().top + window.scrollY <= triggerY) {
            activeId = el.id;
          }
        }

        this._btns.forEach((btn) => {
          btn.classList.toggle('is-active', btn.dataset.anchor === activeId);
        });
      };

      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  customElements.define('sticky-tabs', StickyTabs);
}
